import { Button, Group, Modal, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateGruppe, useUpdateGruppe } from '@/api/useBelegung';
import type { GruppeRow, UUID } from '@/api/types';
import { describeApiError } from './utils';

interface GruppeEditDialogProps {
  opened: boolean;
  planId: UUID;
  gruppe: GruppeRow | null;
  onClose: () => void;
}

interface FormValues {
  name: string;
  aktiv: boolean;
}

/*
 * Die Plätze laut Betriebserlaubnis werden NICHT hier gepflegt, sondern
 * direkt in der Gruppenliste — in der Spalte "Kinder", unter der Zahl der
 * tatsächlich belegten. Zwei Pflegestellen für dieselbe Angabe wären eine
 * zu viel; die Tabelle gewinnt, weil dort auch steht, wogegen sich die
 * Platzzahl vergleicht.
 */

export function GruppeEditDialog({ opened, planId, gruppe, onClose }: GruppeEditDialogProps) {
  const { t } = useTranslation(['belegung', 'common', 'errors']);
  const create = useCreateGruppe(planId);
  const update = useUpdateGruppe(planId);
  const isEdit = gruppe !== null;

  const form = useForm<FormValues>({
    initialValues: { name: gruppe?.name ?? '', aktiv: gruppe?.aktiv ?? true },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : t('belegung:validation.nameRequired')),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({ name: gruppe?.name ?? '', aktiv: gruppe?.aktiv ?? true });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, gruppe]);

  const submit = form.onSubmit(async (values) => {
    try {
      if (isEdit && gruppe) {
        await update.mutateAsync({ id: gruppe.id, patch: values });
        notifications.show({ color: 'teal', message: t('belegung:info.updated') });
      } else {
        await create.mutateAsync(values);
        notifications.show({ color: 'teal', message: t('belegung:info.created') });
      }
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: describeApiError(err) || t('errors:generic'),
      });
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('belegung:form.edit.title') : t('belegung:form.add.title')}
      centered
    >
      <form onSubmit={submit}>
        <Stack>
          <TextInput
            label={t('belegung:form.field.name.label')}
            placeholder={t('belegung:form.field.name.placeholder')}
            required
            {...form.getInputProps('name')}
          />
          <Switch
            label={t('belegung:form.field.aktiv')}
            {...form.getInputProps('aktiv', { type: 'checkbox' })}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {t('common:actions.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
